import { useRef, useCallback } from 'react';
import type React from 'react';
import type { EChartsBaseHandle } from 'components/Charts/EChartsBase';
import type { ChartDataItem } from 'utils/chart-options';
import { getStackedBandAnchorY } from 'utils/chart-options/line/legendTooltip';

// Drives the ECharts native tooltip for legend item hover.
// Sets legendHoveredFieldRef so the tooltip formatter renders that account's single-account
// tooltip (including the summary row), then dispatches showTip at the END of the series so the
// axis pointer (cursor line) moves to the last data point.
// Pass legendHoveredFieldRef into buildLineChartOption via LineChartConfig.legendHoveredFieldNameRef.
export function useLegendSeriesTooltip(
  chartRef: React.RefObject<EChartsBaseHandle | null>,
  chartData: ChartDataItem[] | undefined,
  chartFields: string[],
  lastRenderedDataIndex: number,
  stackedAreaLegendTooltipFields: string[] | undefined,
  totalField?: string,
): {
  legendHoveredFieldRef: React.MutableRefObject<string | null>;
  showTooltipForField: (field: string) => void;
  hideTooltip: () => void;
} {
  const legendHoveredFieldRef = useRef<string | null>(null);

  const showTooltipForField = useCallback(
    (field: string) => {
      if (!chartData?.length || !chartFields?.length) return;
      const seriesIndex = chartFields.indexOf(field);
      if (seriesIndex < 0) return;

      legendHoveredFieldRef.current = field;
      const sourceDataPoint = chartData[chartData.length - 1];

      if (stackedAreaLegendTooltipFields?.includes(field)) {
        const chart = chartRef.current;
        const anchorY = getStackedBandAnchorY(
          field,
          sourceDataPoint,
          chartData,
          chartFields,
          stackedAreaLegendTooltipFields,
          totalField,
        );

        if (chart && anchorY !== null) {
          // null while the chart is (re)initializing — fall through to ECharts' own series
          // anchor below, which needs no coordinate conversion.
          const pixel = chart.convertToPixel({ seriesIndex }, [lastRenderedDataIndex, anchorY]);
          if (Array.isArray(pixel)) {
            chart.dispatchAction({ type: 'showTip', x: pixel[0], y: pixel[1] });
            return;
          }
        }
      }

      chartRef.current?.dispatchAction({ type: 'showTip', seriesIndex, dataIndex: lastRenderedDataIndex });
    },
    [chartRef, chartData, chartFields, lastRenderedDataIndex, stackedAreaLegendTooltipFields, totalField],
  );

  const hideTooltip = useCallback(() => {
    legendHoveredFieldRef.current = null;
    chartRef.current?.dispatchAction({ type: 'hideTip' });
  }, [chartRef]);

  return { legendHoveredFieldRef, showTooltipForField, hideTooltip };
}
